import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { LANG_LABELS, LANG_NATIVE, OFFERED_LANGS, ROLE_LABELS, TIMING_CHOICES, editsCatalog, publishes } from "@speisekarte/core";
import type { Lang, Timing } from "@speisekarte/core";
import { SPAM_FOLDER, account, cancelled, deviceName, passkeysSupported, serverFeatures } from "../account.ts";
import type { Passkey, Session } from "../account.ts";
import { lowerEffects, timingOf } from "../timing.ts";
import { api, messageOf, store, useStore } from "../store.ts";
import { useDraft } from "../kept.ts";
import type { Data, SignedIn } from "../store.ts";
import { LegalLinks } from "./Login.tsx";
import { Team } from "./Team.tsx";
import { EmailState } from "./Email.tsx";
import { LogoPicture } from "./Design.tsx";
import { PhraseLine } from "./TextWork.tsx";

const date = (at: number) => new Date(at).toLocaleDateString("de-DE", { day: "numeric", month: "short", year: "numeric" });

/**
 * "Konto": who is signed in; „Betrieb“ – the house's name and lines on the menus, kept menus, the
 * team; „Mein Zugang“ – name, e-mail address, passkeys, signed-in devices, password. As KellerLog.
 */
export function Account() {
  useStore();
  const [me, setMe] = useState<(SignedIn & { session: string; demo?: boolean }) | null>(null);
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [passkeysOn, setPasskeysOn] = useState(false);

  async function load() {
    setError(null);
    try {
      const [m, p, s, f] = await Promise.all([account.me(), account.passkeys(), account.sessions(), serverFeatures()]);
      setPasskeysOn(f.passkeys);
      setMe(m);
      setPasskeys(p);
      setSessions(s);
    } catch (err) {
      setError(messageOf(err));
    }
  }
  useEffect(() => void load(), []);

  async function signOut() {
    if (!confirm("Auf diesem Gerät abmelden? Im Betrieb bleibt alles.")) return;
    await store.signOut();
    location.hash = "#/anmelden";
  }

  if (!me) {
    return (
      <div className="account">
        {error ? <p className="error-box">{error}</p> : <p className="muted">Lädt …</p>}
        <div className="buttons">
          {error && <button onClick={load}>Noch einmal</button>}
          <button className="danger" onClick={signOut}>
            Abmelden
          </button>
        </div>
      </div>
    );
  }

  const workspace = me.workspaces.find((w) => w.id === store.session?.workspaceId);
  return (
    <div className="account">
      <p className="signed-in">
        Angemeldet als <strong>{me.user.displayName}</strong>
        {workspace && (workspace.role === "owner" ? ` · Inhaber von ${workspace.name}` : ` · ${workspace.name}, Rolle „${ROLE_LABELS[workspace.role]}“`)}
      </p>

      <h2 className="group-title">Betrieb</h2>
      {publishes(store.role) && store.data && <House data={store.data} />}
      {editsCatalog(store.role) && <Keep />}
      {editsCatalog(store.role) && <Languages />}
      {editsCatalog(store.role) && store.data && <TimeControl data={store.data} />}
      {!me.demo && <Team />}

      <h2 className="group-title">Mein Zugang</h2>
      <section className="card">
        <p className="muted">Benutzername: {me.user.username}</p>
        {me.demo ? (
          <p className="hint" data-testid="demo-note">
            Demo-Zugang: Hier darf alles ausprobiert werden. Jede Nacht um 4 Uhr wird der Betrieb zurückgesetzt. Name, Passwort und Team bleiben fest.
          </p>
        ) : (
          <>
            <Field label="Dein Name" value={me.user.displayName} hint="So nennen dich die anderen in der App." save={async (v) => setMe({ ...me, user: (await account.setName(v)).user })} done="Name gespeichert" />
            <Field
              label="E-Mail-Adresse"
              type="email"
              value={me.user.emailPending ?? me.user.email ?? ""}
              hint="Für den Link, falls du dein Passwort vergisst. Nie für Werbung."
              save={async (v) => {
                const { user, sent } = await account.setEmail(v);
                setMe({ ...me, user });
                store.setUser(user);
                return sent ? `Gespeichert. Die Mail zum Bestätigen ist unterwegs. ${SPAM_FOLDER}` : undefined;
              }}
              done="Gespeichert"
            />
            <EmailState
              user={me.user}
              onUser={(user) => {
                setMe({ ...me, user });
                store.setUser(user);
              }}
            />
          </>
        )}
      </section>

      {passkeysOn && !me.demo && (
        <section className="card">
          <h2>Passkeys</h2>
          <p className="muted">Anmelden mit Face ID, Fingerabdruck oder Geräte-PIN statt Passwort. Das Passwort bleibt bestehen.</p>
          <Passkeys passkeys={passkeys} onChange={setPasskeys} />
        </section>
      )}

      <section className="card">
        <h2>Angemeldete Geräte</h2>
        <ul className="list">
          {sessions.map((s) => (
            <li key={s.id} className="entry">
              <div className="entry-text">
                {s.device || "Gerät"} {s.current && <strong>(dieses Gerät)</strong>}
                <br />
                <span className="muted">
                  seit {date(s.createdAt)} · zuletzt {date(s.lastSeenAt)}
                </span>
              </div>
              {!s.current && (
                <button
                  className="link danger entry-action"
                  onClick={async () => {
                    if (!confirm(`„${s.device}“ abmelden? Wer das Gerät hat, kommt dann nicht mehr an die Speisekarten auf dem Server.`)) return;
                    await account.endSession(s.id).catch((err) => setError(messageOf(err)));
                    setSessions(await account.sessions());
                  }}
                >
                  Abmelden
                </button>
              )}
            </li>
          ))}
        </ul>
        <p className="hint">Ein verlorenes Handy hier abmelden.</p>
      </section>

      {!me.demo && (
        <section className="card">
          <details>
            <summary>Passwort ändern</summary>
            <PasswordForm />
          </details>
        </section>
      )}

      {error && <p className="error-box">{error}</p>}
      <div className="buttons">
        <button className="danger" onClick={signOut} data-testid="logout">
          Abmelden
        </button>
      </div>
      <p className="muted legal-foot">
        <LegalLinks />
      </p>
    </div>
  );
}

function House({ data }: { data: Data }) {
  const fields = { house: data.settings.house, address: data.settings.address, imprint: data.settings.imprint, privacy: data.settings.privacy, footer: data.settings.footer, phone: data.settings.phone ?? "", email: data.settings.email ?? "", website: data.settings.website ?? "", instagram: data.settings.instagram ?? "", facebook: data.settings.facebook ?? "", linksInPrint: data.settings.linksInPrint ?? false };
  const [form, setForm] = useDraft("house", JSON.stringify(fields), () => fields);
  const [state, setState] = useState<{ ok?: string; error?: string }>({});
  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      await store.change(() => api("/api/settings", { method: "PUT", body: form }));
      setState({ ok: "Gespeichert" });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  const field = (k: Exclude<keyof typeof form, "linksInPrint">) => ({ value: form[k], onChange: (e: { target: { value: string } }) => [setForm({ ...form, [k]: e.target.value }), setState({})] });
  return (
    <section className="card">
    <form noValidate onSubmit={submit}>
      <h2>Name und Angaben</h2>
      <p className="hint">Stehen auf jeder Speisekarte: der Name oben, der Satz unter den Preisen; Anschrift, Impressum, Datenschutz, Telefon, E-Mail, Website, Instagram und Facebook unten auf der QR-Code-Speisekarte.</p>
      <label>
        Name des Hauses
        <input {...field("house")} maxLength={120} data-testid="house" />
      </label>
      <label>
        Anschrift (im Netz)
        <input {...field("address")} maxLength={300} placeholder="Straße, PLZ Ort" />
      </label>
      <label>
        Link zum Impressum (im Netz)
        <input {...field("imprint")} maxLength={300} placeholder="https://…" inputMode="url" />
      </label>
      <label>
        Link zur Datenschutzerklärung (im Netz)
        <input {...field("privacy")} maxLength={300} placeholder="https://… (leer: steht beim Impressum)" inputMode="url" data-testid="privacy" />
      </label>
      {/* The house's links (S50): on the net at the foot, in print only when asked. */}
      <label>
        Telefon
        <input {...field("phone")} maxLength={40} placeholder="z. B. 06222 9520" inputMode="tel" data-testid="phone" />
      </label>
      <label>
        E-Mail
        <input {...field("email")} maxLength={120} placeholder="info@…" inputMode="email" data-testid="email" />
      </label>
      <label>
        Website
        <input {...field("website")} maxLength={300} placeholder="www.…" inputMode="url" data-testid="website" />
      </label>
      <label>
        Instagram
        <input {...field("instagram")} maxLength={300} placeholder="@name oder Adresse" data-testid="instagram" />
      </label>
      <label>
        Facebook
        <input {...field("facebook")} maxLength={300} placeholder="Name der Seite oder Adresse" data-testid="facebook" />
      </label>
      {(form.phone || form.email || form.website || form.instagram || form.facebook) && (
        <label className="check">
          <input type="checkbox" checked={form.linksInPrint} onChange={(e) => [setForm({ ...form, linksInPrint: e.target.checked }), setState({})]} data-testid="links-in-print" />
          <span>Auch unten auf der gedruckten Karte (PDF, Word)</span>
        </label>
      )}
      <label>
        Satz unter den Preisen
        <input {...field("footer")} maxLength={200} placeholder="Alle Preise in Euro inkl. MwSt." />
      </label>
      {/* Its translation right here (S61 § 4.1); without an own one each card's language has its own words. */}
      {data.settings.footer && form.footer === data.settings.footer && <PhraseLine text={data.settings.footer} data={data} />}
      <button className="primary">Speichern</button>
      {state.ok && <p className="ok-text">{state.ok}</p>}
      {state.error && <p className="error-box">{state.error}</p>}
    </form>
      {/* The logo here too, where one looks for it first (Martin, 25.10.2026); where it stands is a matter of the style (step 2). */}
      <h3 style={{ marginTop: "1.4rem" }}>Logo</h3>
      <p className="hint">Steht oben auf jeder Speisekarte, in Druck, PDF, Word und im Netz. Wo genau und ob statt des Namens, legt der Stil fest (Schritt 2 einer Speisekarte).</p>
      <LogoPicture />
    </section>
  );
}

/** Further languages of the cards (S36): only with one switched on does the app show anything of them. */
function Languages() {
  // A tick shows at once and goes back if the server says no (B4, Martin 02.11.2026): on a slow net nothing seemed to happen.
  const [asked, setAsked] = useState<{ languages?: Lang[]; proofread?: boolean }>({});
  const on = asked.languages ?? store.data?.settings.languages ?? [];
  const [state, setState] = useState<{ ok?: string; error?: string }>({});
  async function toggle(lang: Lang, checked: boolean) {
    const languages = checked ? [...on.filter((l) => l !== lang), lang] : on.filter((l) => l !== lang);
    setAsked((a) => ({ ...a, languages }));
    try {
      await store.change(() => api("/api/settings", { method: "PUT", body: { languages } }));
      setState({ ok: checked ? `${LANG_LABELS[lang]} eingeschaltet` : `${LANG_LABELS[lang]} ausgeschaltet` });
    } catch (err) {
      setState({ error: messageOf(err) });
    } finally {
      setAsked(({ languages: _, ...rest }) => rest);
    }
  }
  // „Deutsch prüfen“ (S47), offered like a further language: „Gastronomen-Deutsch → Deutsch“.
  // With a language it is part of it, on and fixed (S61 § 11, Martin 02.11.2026).
  const withLangs = on.length > 0;
  const proofread = withLangs || (asked.proofread ?? Boolean(store.data?.settings.proofread));
  async function toggleProofread(checked: boolean) {
    setAsked((a) => ({ ...a, proofread: checked }));
    try {
      await store.change(() => api("/api/settings", { method: "PUT", body: { proofread: checked } }));
      setState({ ok: checked ? "Deutsch prüfen eingeschaltet" : "Deutsch prüfen ausgeschaltet" });
    } catch (err) {
      setState({ error: messageOf(err) });
    } finally {
      setAsked(({ proofread: _, ...rest }) => rest);
    }
  }
  return (
    <section className="card" data-testid="languages">
      <h2>Sprachen</h2>
      {/* All five since 01.11.2026: a list, each language with its own name as the guests read it. */}
      <p className="muted small" style={{ margin: "0 0 0.2rem" }}>
        Speisekarten auch auf
      </p>
      <ul className="lang-list-on">
        {OFFERED_LANGS.map((lang) => (
          <li key={lang}>
            <label className="check">
              <input type="checkbox" checked={on.includes(lang)} onChange={(e) => void toggle(lang, e.target.checked)} data-testid={`language-${lang}`} />
              <span>
                {LANG_LABELS[lang]} <span className="muted small">{LANG_NATIVE[lang]}</span>
              </span>
            </label>
          </li>
        ))}
      </ul>
      <label className="check">
        <input type="checkbox" checked={proofread} disabled={withLangs} onChange={(e) => void toggleProofread(e.target.checked)} data-testid="proofread-on" />
        Deutsch prüfen: Rechtschreibung und Typografie{withLangs && <span className="muted small"> · gehört zu den Sprachen</span>}
      </label>
      {state.ok && <p className="ok-text">{state.ok}</p>}
      {state.error && <p className="error">{state.error}</p>}
      {/* The explanation always, not only until the first switch (Martin, 01.11.2026). */}
      <p className="hint">{"Mit einer Sprache hat jede Speise und jedes Getränk ein Feld für die Übersetzung; gedruckt wird auf Wunsch eine eigene Karte oder Deutsch mit der Sprache darunter, im Netz wählen die Gäste. „Texte prüfen und übersetzen“ macht alle Sprachen in einem Gang; von Hand wählst du in den Katalogen unter „Übersetzen in“, welche du bearbeitest. Die Namen der 14 Allergene kommen amtlich aus der EU-Verordnung. Ausschalten löscht keine Übersetzung. „Deutsch prüfen“ findet Tippfehler, Groß und klein, Getrenntes und Abstände („30cm“, „Gem.Salat“) und schlägt jede Änderung einzeln vor; geändert wird nur, was du übernimmst. Mit einer Sprache ist es immer dabei: Geprüft wird vor dem Übersetzen, damit kein Tippfehler in die Sprachen wandert."}</p>
    </section>
  );
}

/**
 * Die Zeitsteuerung (S62; Z0 in 25-zeitsteuerung.md, Bild 16): three choices, each with what it
 * does; after the choice, where it is now. Back to less asks first what that means for the guests
 * (`lowerEffects`); what rests stays kept and comes back with the choice.
 */
function TimeControl({ data }: { data: Data }) {
  const now = timingOf(data);
  const [asking, setAsking] = useState<Timing | null>(null);
  const [state, setState] = useState<{ busy?: boolean; ok?: string; error?: string }>({});
  async function choose(next: Timing) {
    setState({ busy: true });
    setAsking(null);
    try {
      await store.change(() => api("/api/settings", { method: "PUT", body: { timing: next } }));
      setState({ ok: `Zeitsteuerung: ${TIMING_CHOICES.find((c) => c.id === next)!.label}` });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  function pick(next: Timing) {
    if (next === now) return;
    const effects = lowerEffects(data, next);
    if (effects.length) setAsking(next);
    else void choose(next);
  }
  const where =
    now === "off" ? null : now === "standard" ? "Zu finden: an jedem Abschnitt einer Karte „Zeiten im Netz …“; beim Veröffentlichen in Schritt 3 „Jetzt · Morgen · Anderer Zeitpunkt …“; unter „Im Netz“ „Später …“." : "Zu finden: alles aus Standard, dazu unter „Im Netz“ „Wochenplan einrichten …“.";
  return (
    <section className="card" data-testid="timing">
      <h2>Zeitsteuerung</h2>
      <div className="timing-choices" role="radiogroup" aria-label="Zeitsteuerung">
        {TIMING_CHOICES.map((c) => (
          <label key={c.id} className={`timing-choice ${now === c.id ? "on" : ""}`} data-testid={`timing-${c.id}`}>
            <input type="radio" name="timing" checked={now === c.id} disabled={state.busy} onChange={() => pick(c.id)} />
            <span>
              <b>{c.label}</b>
              <span className="small">{c.text}</span>
            </span>
          </label>
        ))}
      </div>
      {asking && (
        <div className="warn-box" data-testid="timing-ask">
          <b>Auf „{TIMING_CHOICES.find((c) => c.id === asking)!.label}“ umstellen?</b>
          {lowerEffects(data, asking).map((e) => (
            <p key={e} style={{ margin: "0.3rem 0 0" }}>
              {e}
            </p>
          ))}
          <p className="small" style={{ margin: "0.3rem 0 0" }}>
            Was ruht, bleibt gespeichert und gilt wieder, wenn du die Stufe wieder wählst.
          </p>
          <div className="buttons" style={{ marginTop: "0.5rem" }}>
            <button className="primary" onClick={() => void choose(asking)} data-testid="timing-ask-yes">
              Umstellen
            </button>
            <button onClick={() => setAsking(null)}>Abbrechen</button>
          </div>
        </div>
      )}
      {where && !asking && (
        <p className="ok-box" data-testid="timing-where">
          {where}
        </p>
      )}
      {state.ok && <p className="ok-text">{state.ok}</p>}
      {state.error && <p className="error">{state.error}</p>}
    </section>
  );
}

/** How many menus stay (S4). */
function Keep() {
  const keep = store.data?.settings.keepMenus ?? 20;
  const [value, setValue] = useDraft("keep", String(keep), () => String(keep));
  const [state, setState] = useState<{ ok?: string; error?: string }>({});
  async function submit(e: FormEvent) {
    e.preventDefault();
    const n = Number(value);
    if (!Number.isInteger(n) || n < 5 || n > 100) return setState({ error: "Bitte eine Zahl von 5 bis 100." });
    try {
      await store.change(() => api("/api/settings", { method: "PUT", body: { keepMenus: n } }));
      setState({ ok: "Gespeichert" });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  return (
    <section className="card">
      <h2>Gespeicherte Speisekarten</h2>
      <form noValidate onSubmit={submit}>
        <label>
          So viele Speisekarten bleiben gespeichert
          <span className="row">
            <input inputMode="numeric" value={value} onChange={(e) => [setValue(e.target.value), setState({})]} style={{ maxWidth: "6rem" }} data-testid="keep-menus" />
            <button className="primary" disabled={value === String(keep)}>Speichern</button>
          </span>
        </label>
        <p className={state.error ? "error" : state.ok ? "ok-text" : "hint"}>{state.error ?? state.ok ?? "Kommt eine neue Speisekarte dazu, geht die älteste. Dazu bleiben immer alle, die gerade im Netz stehen oder am Eingang aushängen."}</p>
      </form>
    </section>
  );
}

function Field(props: { label: string; value: string; hint: string; type?: string; save: (v: string) => Promise<string | void>; done: string }) {
  const [value, setValue] = useState(props.value);
  const [state, setState] = useState<{ ok?: string; error?: string; busy?: boolean }>({});
  async function submit(e: FormEvent) {
    e.preventDefault();
    setState({ busy: true });
    try {
      setState({ ok: (await props.save(value.trim())) || props.done });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  return (
    <form noValidate onSubmit={submit}>
      <label>
        {props.label}
        <span className="row">
          <input type={props.type ?? "text"} value={value} onChange={(e) => [setValue(e.target.value), setState({})]} />
          <button className="primary" disabled={state.busy || value.trim() === props.value}>Speichern</button>
        </span>
      </label>
      <p className={state.error ? "error" : state.ok ? "ok-text" : "hint"}>{state.error ?? state.ok ?? props.hint}</p>
    </form>
  );
}

function Passkeys({ passkeys, onChange }: { passkeys: Passkey[]; onChange: (p: Passkey[]) => void }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function add() {
    setError(null);
    setBusy(true);
    try {
      onChange(await account.addPasskey(deviceName()));
    } catch (err) {
      if (!cancelled(err)) setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      {passkeys.length > 0 && (
        <ul className="list">
          {passkeys.map((p) => (
            <li key={p.id} className="entry">
              <div className="entry-text">
                {p.name}
                <br />
                <span className="muted">
                  angelegt {date(p.createdAt)}
                  {p.lastUsedAt ? ` · zuletzt benutzt ${date(p.lastUsedAt)}` : ""}
                  {p.backedUp ? " · auf deinen Geräten synchronisiert" : ""}
                </span>
              </div>
              <button
                className="link danger entry-action"
                onClick={async () => {
                  if (!confirm(`Passkey „${p.name}“ entfernen? Mit ihm kannst du dich dann nicht mehr anmelden; das Passwort bleibt.`)) return;
                  await account.removePasskey(p.id).catch((err) => setError(messageOf(err)));
                  onChange(await account.passkeys());
                }}
              >
                Entfernen
              </button>
            </li>
          ))}
        </ul>
      )}
      {passkeysSupported() ? (
        <button className="primary" disabled={busy} onClick={add}>
          {busy ? "Passkey hinzufügen …" : "Passkey hinzufügen"}
        </button>
      ) : (
        <p className="warn">Dieser Browser kann keine Passkeys (ab iOS 17.4 und aktuellen Browsern).</p>
      )}
      {error && <p className="error-box">{error}</p>}
    </>
  );
}

function PasswordForm() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [state, setState] = useState<{ ok?: string; error?: string; busy?: boolean }>({});
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!current) return setState({ error: "Bitte gib dein bisheriges Passwort ein." });
    if (next.length < 10) return setState({ error: "Das Passwort braucht mindestens 10 Zeichen." });
    if (next !== repeat) return setState({ error: "Die beiden Passwörter sind nicht gleich." });
    setState({ busy: true });
    try {
      await account.setPassword(current, next);
      setCurrent("");
      setNext("");
      setRepeat("");
      setState({ ok: "Passwort geändert. Deine anderen Geräte sind abgemeldet." });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  return (
    <form noValidate onSubmit={submit}>
      <label>
        Bisheriges Passwort
        <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
      </label>
      <label>
        Neues Passwort
        <input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
        <span className="hint">Mindestens 10 Zeichen.</span>
      </label>
      <label>
        Neues Passwort wiederholen
        <input type="password" value={repeat} onChange={(e) => setRepeat(e.target.value)} autoComplete="new-password" />
      </label>
      <button className="primary" disabled={state.busy}>
        {state.busy ? "Passwort ändern …" : "Passwort ändern"}
      </button>
      {state.error && <p className="error-box">{state.error}</p>}
      {state.ok && <p className="ok-text">{state.ok}</p>}
    </form>
  );
}
